import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { m } from "framer-motion";
import { Search } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { CompanyCard } from "@/companies/CompanyCard";
import type { Company } from "@/companies/company";
import {
  type CompaniesSearch,
  type CompanyTypeFilter,
  coerceSortBy,
  coerceSortDirection,
  coerceTypeFilter,
  toTypeParam,
} from "@/companies/companiesSearch";
import {
  Button,
  Input,
  Pagination,
  Segmented,
  type SegmentedOption,
  Skeleton,
} from "@rumbledirect/ui-v2";
import { useGetAdminCompanies } from "@/lib/api/generated/company/company";
import { useMotionInitial } from "@rumbledirect/ui-v2/motion";
import { staggerContainer } from "@rumbledirect/ui-v2/motion/variants";

const ITEMS_PER_PAGE = 30;

const TYPE_FILTER_OPTIONS: SegmentedOption<CompanyTypeFilter>[] = [
  { value: "all", label: "Alle" },
  { value: "insurance", label: "Verzekeraars" },
  { value: "lease", label: "Lease" },
];
const SKELETON_KEYS = Array.from(
  { length: 8 },
  (_, index) => `company-skeleton-${index}`,
);

export const Route = createFileRoute("/_authenticated/companies")({
  validateSearch: (search: Record<string, unknown>): CompaniesSearch => {
    const page = Number(search.page);
    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      query: typeof search.query === "string" ? search.query : "",
      sortBy: coerceSortBy(search.sortBy),
      sortDirection: coerceSortDirection(search.sortDirection),
      type: coerceTypeFilter(search.type),
    };
  },
  component: CompaniesPage,
});

function CompaniesPage(): ReactNode {
  const { page, query, sortBy, sortDirection, type } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminCompanies(
      {
        page,
        // The collection is not scoped server-side, so the opdrachtgever types
        // are sent on every request — including "Alle" — or dealerships would
        // appear in a directory that is by definition not about them.
        "type[]": toTypeParam(type),
        [`order[${sortBy}]`]: sortDirection,
        ...(query.trim() !== "" ? { search: query.trim() } : {}),
      },
      { query: { placeholderData: keepPreviousData } },
    );

  // Local search box synced to the URL with a small debounce so each keystroke
  // doesn't push a history entry / refetch.
  const [searchInput, setSearchInput] = useState(query);
  useEffect(() => {
    setSearchInput(query);
  }, [query]);
  useEffect(() => {
    if (searchInput === query) return;
    const timer = window.setTimeout(() => {
      void navigate({
        search: (prev) => ({ ...prev, page: 1, query: searchInput }),
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, query, navigate]);

  const totalItems = data?.totalItems ?? 0;
  const members = data?.member;

  // The grid remounts on this key to replay the stagger. It is derived from the
  // data actually on screen, not from the URL: with `keepPreviousData` the URL
  // flips the moment you click while the previous page's cards are still
  // rendered, so keying on the URL animated the stale cards and then animated
  // again when the new data landed. Deriving it from the rendered members keeps
  // it a pure computation — an incrementing counter mutated during render is
  // not safe here, because `members` gets a fresh identity on every render.
  const gridKey =
    members === undefined
      ? "empty"
      : members.map((company) => company["@id"]).join("|");

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Opdrachtgevers
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Beheer relatiegegevens en instellingen per opdrachtgever.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented
          value={type}
          onValueChange={(next) =>
            void navigate({
              search: (prev) => ({ ...prev, page: 1, type: next }),
            })
          }
          options={TYPE_FILTER_OPTIONS}
          size="sm"
          aria-label="Filter op type opdrachtgever"
        />

        <div className="relative w-full sm:w-64">
          <Search
            className="pointer-events-none absolute inset-y-0 left-3 my-auto size-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            aria-label="Zoek opdrachtgevers"
            placeholder="Zoek opdrachtgever"
            className="pl-9"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>
      </div>

      <CompanyGrid
        gridKey={String(gridKey)}
        isPending={isPending}
        isError={isError}
        isPlaceholderData={isPlaceholderData}
        members={members}
        onRetry={() => void refetch()}
      />

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}
    </div>
  );
}

function CompanyGrid({
  gridKey,
  isPending,
  isError,
  isPlaceholderData,
  members,
  onRetry,
}: {
  readonly gridKey: string;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly isPlaceholderData: boolean;
  readonly members: readonly Company[] | undefined;
  readonly onRetry: () => void;
}): ReactNode {
  const motionInitial = useMotionInitial("hidden");
  const gridClassName =
    "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 min-[1180px]:grid-cols-4";

  if (isPending) {
    return (
      <div
        className={gridClassName}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label="Opdrachtgevers laden"
      >
        {SKELETON_KEYS.map((key) => (
          <Skeleton key={key} className="h-24 rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-border/70 bg-surface px-4 py-16 text-center shadow-xs"
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            We konden de opdrachtgevers niet laden.
          </p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Opnieuw proberen
          </Button>
        </div>
      </div>
    );
  }

  if (members === undefined || members.length === 0) {
    return (
      <div className="rounded-lg border border-border/70 bg-surface px-4 py-16 text-center text-sm text-muted-foreground shadow-xs">
        Geen opdrachtgevers gevonden voor deze filters.
      </div>
    );
  }

  return (
    <m.div
      key={gridKey}
      variants={staggerContainer}
      initial={motionInitial}
      animate="visible"
      aria-busy={isPlaceholderData}
      className={gridClassName}
    >
      {members.map((company) => (
        <CompanyCard key={company.id} company={company} />
      ))}
    </m.div>
  );
}
